import { Extension, Mark } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import {
  SUGGESTION_DELETE,
  SUGGESTION_INSERT,
  applyAllSuggestions,
  applySuggestionDecision,
  collectSuggestionRanges,
  suggestionAt,
} from './suggestionHelpers';

export const SuggestionInsert = Mark.create({
  name: SUGGESTION_INSERT,
  excludes: SUGGESTION_DELETE,
  inclusive: false,
  priority: 1000,
  parseHTML() {
    return [{ tag: 'ins[data-suggestion="insert"]' }];
  },
  renderHTML() {
    return ['ins', { 'data-suggestion': 'insert', class: 'suggestion-insert' }, 0];
  },
});

export const SuggestionDelete = Mark.create({
  name: SUGGESTION_DELETE,
  excludes: SUGGESTION_INSERT,
  inclusive: false,
  priority: 1000,
  parseHTML() {
    return [{ tag: 'del[data-suggestion="delete"]' }];
  },
  renderHTML() {
    return ['del', { 'data-suggestion': 'delete', class: 'suggestion-delete' }, 0];
  },
});

const insertSuggestedText = (view, from, to, text) => {
  const { state } = view;
  const insertType = state.schema.marks[SUGGESTION_INSERT];
  const deleteType = state.schema.marks[SUGGESTION_DELETE];
  if (!insertType || !text) {
    return false;
  }
  let tr = state.tr;
  let insertAt = from;
  if (from !== to && deleteType) {
    tr.addMark(from, to, deleteType.create());
    insertAt = to;
  } else {
    const current = suggestionAt(state.doc, from);
    if (current?.type === SUGGESTION_DELETE) {
      insertAt = current.to;
    }
  }
  tr.insertText(text, insertAt);
  tr.addMark(insertAt, insertAt + text.length, insertType.create());
  tr.setSelection(TextSelection.create(tr.doc, insertAt + text.length));
  view.dispatch(tr.scrollIntoView());
  return true;
};

const Suggestions = Extension.create({
  name: 'suggestions',

  addStorage() {
    return { enabled: false };
  },

  addCommands() {
    return {
      setSuggesting:
        (enabled) =>
        () => {
          this.storage.enabled = Boolean(enabled);
          return true;
        },
      acceptSuggestion:
        () =>
        ({ state, tr, dispatch }) => {
          const range = suggestionAt(state.doc, state.selection.from);
          if (!range) {
            return false;
          }
          applySuggestionDecision(tr, state.schema, range, true);
          dispatch?.(tr);
          return true;
        },
      rejectSuggestion:
        () =>
        ({ state, tr, dispatch }) => {
          const range = suggestionAt(state.doc, state.selection.from);
          if (!range) {
            return false;
          }
          applySuggestionDecision(tr, state.schema, range, false);
          dispatch?.(tr);
          return true;
        },
      acceptAllSuggestions:
        () =>
        ({ state, tr, dispatch }) => {
          if (!collectSuggestionRanges(state.doc).length) {
            return false;
          }
          applyAllSuggestions(tr, state.schema, true);
          dispatch?.(tr);
          return true;
        },
      rejectAllSuggestions:
        () =>
        ({ state, tr, dispatch }) => {
          if (!collectSuggestionRanges(state.doc).length) {
            return false;
          }
          applyAllSuggestions(tr, state.schema, false);
          dispatch?.(tr);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const getEnabled = () => this.storage.enabled;
    return [
      new Plugin({
        key: new PluginKey('liwazaSuggestions'),
        props: {
          handleTextInput(view, from, to, text) {
            if (!getEnabled()) {
              return false;
            }
            return insertSuggestedText(view, from, to, text);
          },
          handlePaste(view, event) {
            if (!getEnabled()) {
              return false;
            }
            const text = event.clipboardData?.getData('text/plain') || '';
            if (!text) {
              return false;
            }
            event.preventDefault();
            const { from, to } = view.state.selection;
            return insertSuggestedText(view, from, to, text);
          },
          handleKeyDown(view, event) {
            if (!getEnabled()) {
              return false;
            }
            if (event.key !== 'Backspace' && event.key !== 'Delete') {
              return false;
            }
            const { state } = view;
            const deleteType = state.schema.marks[SUGGESTION_DELETE];
            const insertType = state.schema.marks[SUGGESTION_INSERT];
            const { from, to, empty } = state.selection;
            let start = from;
            let end = to;
            if (empty) {
              if (event.key === 'Backspace') {
                if (from <= 1) {
                  return true;
                }
                start = from - 1;
                end = from;
              } else {
                if (from >= state.doc.content.size) {
                  return true;
                }
                start = from;
                end = from + 1;
              }
            }
            if (start >= end) {
              return true;
            }
            const insideInsert = insertType && state.doc.rangeHasMark(start, end, insertType);
            let tr = state.tr;
            if (insideInsert) {
              tr.delete(start, end);
            } else if (deleteType) {
              tr.addMark(start, end, deleteType.create());
              const caret = event.key === 'Backspace' ? start : end;
              tr.setSelection(TextSelection.create(tr.doc, caret));
            }
            view.dispatch(tr.scrollIntoView());
            return true;
          },
        },
      }),
    ];
  },
});

export default Suggestions;
